import { RadioGroupInput, Typography, handleApiRequest } from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationEmployeeCooperationStatusChoices,
  UpdateEmployeeCooperationStatusMutation,
  UpdateEmployeeCooperationStatusMutationVariables,
  useUpdateEmployeeCooperationStatusMutation,
} from "@graphql/generated";
import { Button, Modal, ModalBody, ModalContent, ModalProps } from "@heroui/react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export type ChangeStatusModalProps = Pick<ModalProps, "isOpen" | "onClose" | "onOpenChange"> & {
  refreshData?: () => void;
  status?: AuthAccountOrganizationEmployeeCooperationStatusChoices;
  cooperationId?: string;
};

export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  refreshData,
  status,
  cooperationId,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<
    AuthAccountOrganizationEmployeeCooperationStatusChoices | undefined
  >(status);

  const [updateEmployeeCooperationStatus, { loading }] = useUpdateEmployeeCooperationStatusMutation();

  const handleSelectStatus = async () => {
    if (!cooperationId || !selectedStatus) {
      toast.error("Please select a status");
      return;
    }

    await handleApiRequest<UpdateEmployeeCooperationStatusMutation, UpdateEmployeeCooperationStatusMutationVariables>({
      apiFunction: updateEmployeeCooperationStatus,
      variables: {
        id: cooperationId,
        status: selectedStatus,
      },
      componentName: "ChangeStatusModal",
      actionName: "updateEmployeeCooperationStatus",
      onSuccess: () => {
        refreshData?.();
        onClose?.();
      },
      successMessage: "Employee status updated successfully",
      fallbackErrorMessage: "Failed to update employee status",
      extraErrorContext: {
        cooperationId: cooperationId,
        newStatus: selectedStatus,
      },
    });
  };

  useEffect(() => {
    setSelectedStatus(status);
  }, [status]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} onOpenChange={onOpenChange} size="2xl">
      <ModalContent className="p-4">
        <ModalBody className="flex items-center gap-8">
          <RadioGroupInput
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e?.target?.value as AuthAccountOrganizationEmployeeCooperationStatusChoices);
            }}
            classNames={{
              label: "text-lg text-black",
            }}
            orientation="vertical"
            label={"Which status do you want to change the employee status to?"}
            items={[
              {
                label: (
                  <CustomLabel
                    title="Active"
                    description="The employee is currently active and working for the organization."
                  />
                ),
                value: AuthAccountOrganizationEmployeeCooperationStatusChoices.ACTIVE,
              },
              {
                label: (
                  <CustomLabel
                    title="Suspended"
                    description="The employee is currently suspended and cannot work for the organization."
                  />
                ),
                value: AuthAccountOrganizationEmployeeCooperationStatusChoices.SUSPENDED,
              },
              {
                label: (
                  <CustomLabel
                    title="Fired"
                    description="The employee is currently fired and cannot work for the organization."
                  />
                ),
                value: AuthAccountOrganizationEmployeeCooperationStatusChoices.FIRED,
              },
              {
                label: (
                  <CustomLabel
                    title="Ended"
                    description="The employee is currently ended and cannot work for the organization."
                  />
                ),
                value: AuthAccountOrganizationEmployeeCooperationStatusChoices.FINISHED,
              },
            ]}
          />
          <Button
            color="success"
            className="w-60"
            onClick={handleSelectStatus}
            isLoading={loading}
            isDisabled={loading}
          >
            Save
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CustomLabel = ({ title, description }: { title: string; description: string }) => {
  return (
    <div>
      <Typography className="text-black">{title}</Typography>
      <Typography variant="muted">{description}</Typography>
    </div>
  );
};
